// Live step-by-step run progress. Subscribes to runs/{runId}/steps via
// useRunSteps; each step doc carries kind + payload + timestamp + seq.
// Status colors per PRD: idle (Schist) / running (Running amber) /
// passed (Passed moss-green) / failed (Failed iron-rust).
//
// Step rows are expandable to show screenshot + logs payload.

import { useMemo, useState } from 'react'

const KIND_COLOR = {
  runStarted: 'schist',
  nodeEntered: 'running',
  assertResult: 'passed',
  transitionFired: 'schist',
  nodeCompleted: 'passed',
  runCompleted: 'passed',
  screenshot: 'schist',
  log: 'schist',
  error: 'failed',
}

const COLOR_CLASSES = {
  schist: {
    icon: 'text-[var(--color-schist)]',
    bg: 'bg-[var(--color-sandstone)]/60',
    text: 'text-[var(--color-slate)]',
  },
  running: {
    icon: 'text-[var(--color-running)]',
    bg: 'bg-[var(--color-running)]/10',
    text: 'text-[var(--color-running)]',
  },
  passed: {
    icon: 'text-[var(--color-passed)]',
    bg: 'bg-[var(--color-passed)]/10',
    text: 'text-[var(--color-passed)]',
  },
  failed: {
    icon: 'text-[var(--color-failed)]',
    bg: 'bg-[var(--color-failed)]/10',
    text: 'text-[var(--color-failed)]',
  },
}

function colorOf(step) {
  if (step.kind === 'nodeCompleted') {
    if (step.payload?.status === 'failed') return 'failed'
    if (step.payload?.status === 'passed') return 'passed'
    return 'schist'
  }
  if (step.kind === 'runCompleted') {
    return step.payload?.status === 'failed' ? 'failed' : 'passed'
  }
  if (step.kind === 'assertResult') {
    return step.payload?.passed === false ? 'failed' : 'passed'
  }
  return KIND_COLOR[step.kind] ?? 'schist'
}

function iconFor(step) {
  const color = colorOf(step)
  if (color === 'passed') return '✓'
  if (color === 'failed') return '✕'
  if (color === 'running') return '◌'
  return '·'
}

function summarize(step) {
  const p = step.payload ?? {}
  switch (step.kind) {
    case 'runStarted':
      return `Run started — ${p.flowName ?? ''}${p.env ? ` (${p.env})` : ''}`
    case 'nodeEntered':
      return `Entered ${p.nodeId}`
    case 'assertResult':
      return `${p.passed ? 'Assert passed' : 'Assert failed'} — ${formatAssert(p.assert)}${
        p.reason ? `: ${p.reason}` : ''
      }`
    case 'transitionFired':
      return `${p.fromNodeId} → ${p.toNodeId} (${p.on})`
    case 'nodeCompleted':
      return `${p.nodeId} ${p.status}`
    case 'runCompleted':
      return `Run ${p.status}${p.durationMs ? ` in ${(p.durationMs / 1000).toFixed(2)}s` : ''}`
    case 'screenshot':
      return `Screenshot — ${p.nodeId}`
    case 'log':
      return `[${p.level}] ${p.message}`
    case 'error':
      return `Error${p.nodeId ? ` at ${p.nodeId}` : ''} — ${p.message}`
    default:
      return step.kind
  }
}

function formatAssert(a) {
  if (!a) return ''
  if (a.role && a.text) return `role=${a.role} text="${a.text}"`
  if (a.role) return `role=${a.role}`
  if (a.testId) return `testId=${a.testId}`
  return Object.entries(a)
    .slice(0, 2)
    .map(([k, v]) => `${k}=${v}`)
    .join(' ')
}

function StepRow({ step, expanded, onToggle }) {
  const color = colorOf(step)
  const cls = COLOR_CLASSES[color]
  const hasDetails =
    step.kind === 'screenshot' ||
    step.kind === 'log' ||
    step.kind === 'error' ||
    (step.payload && Object.keys(step.payload).length > 1)
  const time = step.timestamp ? new Date(step.timestamp).toLocaleTimeString() : ''

  return (
    <li className={`border-t border-[var(--color-sandstone)] ${cls.bg}`}>
      <button
        type="button"
        onClick={hasDetails ? onToggle : undefined}
        aria-expanded={hasDetails ? expanded : undefined}
        className={`flex w-full items-center gap-3 px-4 py-2 text-left ${hasDetails ? 'cursor-pointer hover:bg-[var(--color-sandstone)]/60' : 'cursor-default'}`}
      >
        <span className={`shrink-0 font-mono text-sm ${cls.icon}`} aria-hidden="true">
          {iconFor(step)}
        </span>
        <span className="shrink-0 w-12 font-mono text-[11px] text-[var(--color-schist)]">
          {String(step.seq ?? '').padStart(4, '0')}
        </span>
        <span className={`flex-1 text-sm ${cls.text}`}>{summarize(step)}</span>
        {time && <span className="font-mono text-[11px] text-[var(--color-schist)]">{time}</span>}
        {hasDetails && (
          <span
            aria-hidden="true"
            className={`shrink-0 text-xs text-[var(--color-schist)] transition-transform ${expanded ? 'rotate-90' : ''}`}
          >
            ▶
          </span>
        )}
      </button>
      {hasDetails && expanded && (
        <div className="border-t border-[var(--color-sandstone)] bg-[var(--color-limestone)] px-4 py-3">
          {step.kind === 'screenshot' && step.payload?.url && (
            <img
              src={step.payload.url}
              alt={`Screenshot at ${step.payload.nodeId}`}
              className="max-w-md rounded-md border border-[var(--color-sandstone)]"
            />
          )}
          <pre className="overflow-x-auto whitespace-pre-wrap rounded-md bg-[var(--color-sandstone)]/40 px-3 py-2 font-mono text-xs text-[var(--color-slate)]">
            {JSON.stringify(step.payload ?? {}, null, 2)}
          </pre>
        </div>
      )}
    </li>
  )
}

export function RunStepList({ steps, loading, error }) {
  const [expanded, setExpanded] = useState(() => new Set())
  const ordered = useMemo(
    () => [...(steps ?? [])].sort((a, b) => (a.seq ?? 0) - (b.seq ?? 0)),
    [steps],
  )

  function toggle(id) {
    setExpanded((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  if (error) {
    return (
      <p className="px-4 py-3 text-sm text-[var(--color-failed)]" role="alert">
        Failed to load steps: {error.message}
      </p>
    )
  }
  if (loading && ordered.length === 0) {
    return <p className="px-4 py-3 text-sm text-[var(--color-schist)]">Waiting for steps…</p>
  }
  if (ordered.length === 0) {
    return (
      <p className="px-4 py-3 text-sm text-[var(--color-schist)]">
        No steps yet. The run will populate this list as the worker executes the flow.
      </p>
    )
  }

  return (
    <ul aria-label="Run step log" className="overflow-hidden rounded-md">
      {ordered.map((step) => (
        <StepRow
          key={step.id}
          step={step}
          expanded={expanded.has(step.id)}
          onToggle={() => toggle(step.id)}
        />
      ))}
    </ul>
  )
}
